/**
 * CleanersDemoAccountPage — Iter-111 · Fix 3.
 * Dedicated, self-contained account page for one of the 9 Cleaners CC
 * commercial demo accounts. Admin-only (CEO / Directors). Route:
 * /cleaners-demo/:type · Zero Valet data — everything renders from the
 * account's own document.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, Sparkles, MapPin, ClipboardList, AlertTriangle, Route as RouteIcon, X } from 'lucide-react';
import { Card } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ADMIN_ROLES = ['admin', 'company_manager'];

const TOUR_STEPS = [
  { key: 'violations', title: 'Violations', text: 'Live violation feed for this property type — severity, area and status at a glance.' },
  { key: 'routes', title: 'Routes', text: 'Cleaning routes with crew leaders, stops and live status for this account.' },
  { key: 'amenities', title: 'Curated Amenities', text: 'The pre-configured service touchpoints NECS covers for this property type.' },
];

export default function CleanersDemoAccountPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [account, setAccount] = useState(null);
  const [err, setErr] = useState(null);
  const [tourStep, setTourStep] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user || !ADMIN_ROLES.includes((user.role || '').toLowerCase())) {
      navigate('/login');
      return;
    }
    fetch(`${API}/api/cleaners/demo-accounts/${type}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Failed to load demo account');
        return r.json();
      })
      .then((d) => {
        setAccount(d);
        const seenKey = `necs.cleaners_demo_tour.${type}`;
        if (!localStorage.getItem(seenKey)) {
          setTourStep(0);
          localStorage.setItem(seenKey, '1');
        }
      })
      .catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, user, authLoading]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white" style={{ backgroundColor: '#111' }}>
        <p className="text-sm" data-testid="cleaners-demo-error">{err} — <Link to="/cleaners-command-center" className="underline text-[#22d3ee]">Back to Cleaners CC</Link></p>
      </div>
    );
  }
  if (!account) return <div className="min-h-screen" style={{ backgroundColor: '#111' }} />;

  const accent = account.accent || '#22d3ee';
  const board = account.board || {};

  return (
    <div className="min-h-screen text-white pb-16" style={{ backgroundColor: '#101014' }} data-testid="cleaners-demo-account-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 space-y-6">
        <Link to="/cleaners-command-center" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: accent }} data-testid="cleaners-demo-back">
          <ChevronLeft className="h-4 w-4" /> Cleaners Command Center
        </Link>

        {/* Branding header */}
        <div className="rounded-2xl p-6 border-2" style={{ borderColor: accent, background: 'linear-gradient(135deg,#0a0a0a 0%,#1a1a1a 70%)' }} data-testid="cleaners-demo-hero">
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: accent }}>
            {account.necs_name} · Cleaners CC Demo Account · Internal Review
          </p>
          <h1 className="text-3xl font-black mt-1" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="cleaners-demo-title">
            {account.property_name}
          </h1>
          <p className="text-xs text-zinc-400 mt-1 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5" style={{ color: accent }} /> {account.address} · {account.label}
          </p>
          <p className="text-[11px] mt-2" style={{ color: accent }} data-testid="cleaners-demo-login">
            Demo login: <b>{account.login?.email}</b> / <b>{account.login?.password}</b>
          </p>
        </div>

        {/* Property details */}
        <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="cleaners-demo-details">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>Property Details</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(account.details || {}).map(([k, v]) => (
              <div key={k} className="rounded px-3 py-2" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${accent}55` }}>
                <p className="text-[9px] uppercase tracking-widest text-zinc-500">{k.replace(/_/g, ' ')}</p>
                <p className="text-sm font-black text-white">{String(v)}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Packages / service lines */}
        <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="cleaners-demo-packages">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>
            {account.necs_name} · Service Packages
          </p>
          <div className="flex flex-wrap gap-2">
            {(account.packages || []).map((p) => (
              <span key={p} className="text-xs font-bold px-3 py-1.5 rounded-full" style={{ border: `1px solid ${accent}`, color: accent }}>
                <Sparkles className="h-3 w-3 inline mr-1" />{p}
              </span>
            ))}
          </div>
        </Card>

        {/* Amenities */}
        <Card className="bg-zinc-900 border-zinc-700 p-5 relative" data-testid="cleaners-demo-amenities">
          <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>Curated Amenities — Serviced by NECS</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {(account.amenities || []).map((a) => (
              <div key={a.name} className="flex items-center justify-between gap-2 rounded px-2.5 py-1.5" style={{ backgroundColor: '#0f0f0f', border: `1px solid ${accent}55` }}>
                <span className="text-xs text-white truncate">{a.name}</span>
                <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ color: accent, border: `1px solid ${accent}` }}>×{a.quantity}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Board — violations / routes / tasks / assignments */}
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="bg-zinc-900 border-zinc-700 p-5 relative" data-testid="cleaners-demo-violations">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <AlertTriangle className="h-3.5 w-3.5" /> Violations
            </p>
            <div className="space-y-2">
              {(board.violations || []).map((v) => (
                <div key={v.violation_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{(v.violation_type || '').replace(/_/g, ' ')} · {v.area}</p>
                    <p className="text-[10px] text-zinc-500">by {v.reported_by} · severity {v.severity}</p>
                  </div>
                  <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full flex-shrink-0"
                        style={{ color: v.status === 'resolved' ? '#34d399' : '#fbbf24', border: `1px solid ${v.status === 'resolved' ? '#34d399' : '#fbbf24'}` }}>
                    {v.status}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700 p-5 relative" data-testid="cleaners-demo-routes">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <RouteIcon className="h-3.5 w-3.5" /> Routes
            </p>
            <div className="space-y-2">
              {(board.routes || []).map((r) => (
                <div key={r.route_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <div className="min-w-0">
                    <p className="font-bold text-white truncate">{r.route_name}</p>
                    <p className="text-[10px] text-zinc-500">Crew Leader {r.crew_leader} · {r.stops} stops</p>
                  </div>
                  <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full flex-shrink-0" style={{ color: accent, border: `1px solid ${accent}` }}>{r.status.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="cleaners-demo-tasks">
            <p className="text-xs uppercase tracking-widest font-bold mb-3 flex items-center gap-1.5" style={{ color: accent }}>
              <ClipboardList className="h-3.5 w-3.5" /> Tasks
            </p>
            <div className="space-y-2">
              {(board.tasks || []).map((t) => (
                <div key={t.task_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <p className="font-semibold text-white truncate">{t.title}</p>
                  <span className="text-[10px] text-zinc-400 flex-shrink-0">{t.assignee} · {t.status}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className="bg-zinc-900 border-zinc-700 p-5" data-testid="cleaners-demo-assignments">
            <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: accent }}>Crew Assignments</p>
            <div className="space-y-2">
              {(board.assignments || []).map((a) => (
                <div key={a.assignment_id} className="flex items-center justify-between gap-2 rounded px-3 py-2 text-xs" style={{ backgroundColor: '#0f0f0f', border: '1px solid #27272a' }}>
                  <p className="font-semibold text-white">{a.agent}</p>
                  <span className="text-[10px] text-zinc-400">{a.zone} · {a.shift} shift</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* First-load demo tour overlay — violations → routes → amenities */}
      {tourStep !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }} data-testid="cleaners-demo-tour">
          <div className="max-w-sm w-full rounded-2xl p-6" style={{ backgroundColor: '#1a1a1a', border: `2px solid ${accent}` }}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-widest font-black" style={{ color: accent }}>
                Demo Tour · {tourStep + 1} / {TOUR_STEPS.length}
              </p>
              <button type="button" onClick={() => setTourStep(null)} className="text-zinc-400 hover:text-white" data-testid="cleaners-demo-tour-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="text-lg font-black text-white mb-1">{TOUR_STEPS[tourStep].title}</h3>
            <p className="text-xs text-zinc-300 leading-relaxed mb-4">{TOUR_STEPS[tourStep].text}</p>
            <div className="flex justify-end gap-2">
              {tourStep < TOUR_STEPS.length - 1 ? (
                <button type="button" onClick={() => setTourStep(tourStep + 1)}
                        className="text-xs font-black px-4 py-2 rounded-full" style={{ backgroundColor: accent, color: '#0a0a0a' }}
                        data-testid="cleaners-demo-tour-next">
                  Next →
                </button>
              ) : (
                <button type="button" onClick={() => setTourStep(null)}
                        className="text-xs font-black px-4 py-2 rounded-full" style={{ backgroundColor: accent, color: '#0a0a0a' }}
                        data-testid="cleaners-demo-tour-done">
                  Start Exploring
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
